// AlbumTracksPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Typography, List, Button, Spin, message,
  Avatar, Checkbox
} from "antd";
import FloatingCartButton from "./FloatingCartButton";

const { Title } = Typography;

const LS_KEY = "simolapps_state";
const API = {
  albumInfo     : "https://simolmedia.tw1.ru/api/get_albums_with_count.php",
  tracksByAlbum : "https://simolmedia.tw1.ru/api/get_tracks_by_album.php",
  queue         : "https://simolmedia.tw1.ru/api/get_download_queue.php",
  purchased     : "https://simolmedia.tw1.ru/api/get_purchased_tracks.php",
  addToQueue    : "https://simolmedia.tw1.ru/api/add_to_download_queue.php"
};

export default function AlbumTracksPage() {
  const { id: albumId } = useParams();
  const location        = useLocation();
  const navigate        = useNavigate();

  const [album,        setAlbum]     = useState(null);
  const [tracks,       setTracks]    = useState([]);
  const [selected,     setSelected]  = useState([]);

  const [queueIds,     setQueueIds]  = useState([]);
  const [purchasedIds, setPurchased] = useState([]);
  const [loading,      setLoading]   = useState(true);

  const userId = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY))?.user?.id ?? null; }
    catch { return null; }
  }, []);

  /* ---------- Загрузка ---------- */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        /* 1. альбом + треки */
        const [albumsArr, trResp] = await Promise.all([
          fetch(API.albumInfo).then(r => r.json()),
          fetch(`${API.tracksByAlbum}?album_id=${albumId}`).then(r => r.json())
        ]);

        const allAlbumTracks = (trResp || []).map(t => ({ ...t, id:Number(t.id) }));
        setAlbum(albumsArr.find(a => String(a.id) === String(albumId)) || null);
        setTracks(allAlbumTracks);

        /* 2. корзина + купленные (если авторизован) */
        if (userId) {
          const [q, p] = await Promise.all([
            fetch(`${API.queue}?user_id=${userId}`).then(r => r.json()),
            fetch(API.purchased,{
              method :"POST",
              headers:{"Content-Type":"application/json"},
              body   : JSON.stringify({ user_id:userId })
            }).then(r => r.json())
          ]);

          /* ════ 1. достаём id из нового формата ответа */
          let qIds = [];
          if (Array.isArray(q.track_ids))      qIds = q.track_ids;
          else if (Array.isArray(q.paid))      qIds = q.paid.map(t => Number(t.id));
          else if (Array.isArray(q.tracks))    qIds = q.tracks.map(t => Number(t.id));
          else                                 qIds = [];

          const pIds = (p.track_ids ?? []).map(Number);

          setQueueIds(   qIds.map(Number) );
          setPurchased(  pIds );
        }

        /* DEBUG-логи оставляем */
        console.log("%c[TDEBUG] Треки альбома:", "color:#0af", allAlbumTracks);
        console.log("%c[TDEBUG] Купленные id:", "color:#e91e63", purchasedIds);
        console.log("%c[TDEBUG] В корзине id:", "color:#ff9800", queueIds);

      } catch(e) {
        console.error(e);
        message.error("Не удалось загрузить данные");
      } finally {
        setLoading(false);
      }
    })();
  }, [albumId,userId]);

  /* ---------- чек-боксы ---------- */
  const toggleTrack = (id, checked, disabled) => {
    if (disabled) {                             // ════ 2. показываем info
      message.info("Этот трек уже куплен");
      return;
    }
    setSelected(cur => checked ? [...cur, id] : cur.filter(x => x !== id));
  };

  const addableIds = tracks
    .filter(t => !queueIds.includes(t.id) && !purchasedIds.includes(t.id))
    .map(t => t.id);

  const toggleAll = () =>
    setSelected(selected.length === addableIds.length ? [] : addableIds);

  /* ---------- отправка ---------- */
  const addToQueue = async () => {
    const newIds = selected.filter(
      id => !queueIds.includes(id) && !purchasedIds.includes(id)
    );
    if (!userId)           return message.error("Авторизуйтесь");
    if (!newIds.length)    return;

    try {
      const r = await fetch(API.addToQueue, {
        method : "POST",
        headers: { "Content-Type":"application/json" },
        body   : JSON.stringify({ user_id:userId, track_ids:newIds })
      }).then(x => x.json());

      if (r.success) {
        message.success("Добавлено в корзину");
        setQueueIds(q => [...q, ...newIds]);
        setSelected([]);
      } else {
        message.error(r.message || "Ошибка добавления");
      }
    } catch { message.error("Ошибка сети"); }
  };

  /* ---------- UI ---------- */
  if (loading)  return <Spin/>;
  if (!album)   return <p>Альбом не найден</p>;

  return (
    <div style={{ padding:20 }}>
      <Button type="link" onClick={() => navigate(-1)} style={{ marginBottom:8 }}>
        ← Назад
      </Button>

      {/* шапка альбома */}
      <div style={{ display:"flex", gap:20, marginBottom:20 }}>
        <img
          src={
            album.thumbnail
              ? `https://audiobuzz-web.tmweb.ru/uploads/thumbnails/albums/${album.thumbnail}`
              : "https://audiobuzz-web.tmweb.ru/uploads/thumbnails/default_cover.jpg"
          }
          alt={album.name}
          style={{ width:120, height:120, objectFit:"cover", borderRadius:12 }}
        />
        <div>
          <Title level={3} style={{ margin:0 }}>
            {location.state?.albumName || album.name}
          </Title>
          <p style={{ color:"#888" }}>{tracks.length} трек(ов)</p>
          {tracks.length > 0 && (
            <Button size="small" onClick={toggleAll}>
              {selected.length === addableIds.length ? "Снять всё" : "Выбрать всё"}
            </Button>
          )}
        </div>
      </div>

      {/* список треков */}
      <List
        dataSource={tracks}
        renderItem={t => {
          const disabled = purchasedIds.includes(t.id);          // куплен?
          const checked  =
            disabled || queueIds.includes(t.id) || selected.includes(t.id);

          const cover = t.track_cover_photo
            ? `https://audiobuzz-web.tmweb.ru/uploads/thumbnails/media/${t.track_cover_photo}`
            : "https://audiobuzz-web.tmweb.ru/uploads/thumbnails/default_cover.jpg";

          return (
            <List.Item>
              <Checkbox
                disabled={disabled}
                checked={checked}
                onChange={e => toggleTrack(t.id, e.target.checked, disabled)}
                style={{ marginRight:16, opacity: disabled ? .4 : 1 }}
              />
              <List.Item.Meta
                avatar={<Avatar shape="square" size={64} src={cover} />}
                title={t.title}
                description={`Длительность: ${Math.floor(t.duration/1000)} сек`}
              />
            </List.Item>
          );
        }}
      />

      {/* кнопка отправки */}
      <div style={{ marginTop:24, textAlign:"center" }}>
        <Button type="primary" disabled={!selected.length} onClick={addToQueue}>
          Добавить в корзину ({selected.length})
        </Button>
      </div>

      <FloatingCartButton/>
    </div>
  );
}
